/**
 * Login — вход команды Skrezo (логин+пароль). Непубличный контур
 * (ADMIN_SPEC §0.1). Вёрстка по макету `design_handoff_skrezo_admin/
 * Skrezo Login.dc.html`: сплит-лейаут, брендовая навью-панель с
 * анимированным фоном слева + форма справа.
 *
 * Логика входа — прежняя: RHF + zodResolver + useMutation(loginTeam,
 * пока мок) → team.setSession → /dashboard. Точка подключения BE — в
 * `services/auth/loginTeam`, разметку это не затрагивает.
 *
 * Цвета формы — токены темы (bg-primary, border-input, ring). Навью-панель
 * (#152449) и её ambient-слои — брендовые, one-off, заданы локально.
 */

import type {CSSProperties} from 'react';
import {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMutation} from '@tanstack/react-query';
import {ArrowRight, Eye, EyeOff, Loader2, Lock, User} from 'lucide-react';

import {loginTeam, type TeamLoginRequest, TeamLoginRequestSchema} from '@services/auth';
import {env} from '@services/utils';
import {useAuth} from '@hooks/useAuth';
import {formatKgPhone, phoneDigits} from '@utils/phone';

import {mapAuthError} from './Login.utils';

const NAVY = '#152449';

const SECTIONS = ['Дашборд', 'Клиенты', 'Тарифы', 'Платежи', 'Аудит'];

/* Ambient-слои навью-панели (radial-подсветки + dot-сетки с дрейфом). */
const glowTop: CSSProperties = {
  background: 'radial-gradient(120% 90% at 12% 8%, rgba(64,109,230,.38) 0%, rgba(21,36,73,0) 55%)',
  animation: 'glowPulse 11s ease-in-out infinite',
};
const glowBottom: CSSProperties = {
  background:
    'radial-gradient(80% 70% at 100% 100%, rgba(14,147,132,.20) 0%, rgba(21,36,73,0) 60%)',
  animation: 'glowPulse 14s ease-in-out infinite reverse',
};
const dotsA: CSSProperties = {
  inset: -40,
  backgroundImage: 'radial-gradient(rgba(255,255,255,.07) 1px, transparent 1px)',
  backgroundSize: '22px 22px',
  animation: 'driftA 32s linear infinite',
};
const dotsB: CSSProperties = {
  inset: -40,
  backgroundImage: 'radial-gradient(rgba(143,180,255,.06) 1px, transparent 1px)',
  backgroundSize: '34px 34px',
  animation: 'driftB 48s linear infinite',
};

export const Login = () => {
  const navigate = useNavigate();
  const {team} = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {control, handleSubmit, watch} = useForm<TeamLoginRequest>({
    resolver: zodResolver(TeamLoginRequestSchema),
    defaultValues: {
      login: env.VITE_DEV_TEAM_LOGIN || '',
      password: env.VITE_DEV_TEAM_PASSWORD || '',
    },
    mode: 'onTouched',
  });

  const mutation = useMutation({
    mutationFn: loginTeam,
    onSuccess: ({accessToken, user, mustChangePassword}) => {
      team.setSession(accessToken, user);
      // Временный пароль — сначала свой, потом панель
      navigate(mustChangePassword ? '/change-password' : '/dashboard', {
        replace: true,
        state: {forced: mustChangePassword},
      });
    },
  });

  const handleFormSubmit = handleSubmit((values) => mutation.mutate(values));
  const errorMessage = mutation.isError ? mapAuthError(mutation.error) : null;

  /** Маска: в стейте формы — сырые цифры, в поле — «700 123 456» */
  const handlePhoneChange = (onChange: (value: string) => void) => (e: {target: {value: string}}) =>
    onChange(phoneDigits(e.target.value));

  const values = watch();
  const canSubmit =
    phoneDigits(values.login ?? '').length === 9 && Boolean(values.password?.trim());

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* LEFT: brand panel */}
      <div
        className="relative hidden shrink-0 basis-[47%] flex-col justify-between overflow-hidden px-[46px] py-[42px] text-white lg:flex"
        style={{background: NAVY}}
      >
        <div className="pointer-events-none absolute inset-0" style={glowTop} />
        <div className="pointer-events-none absolute inset-0" style={glowBottom} />
        <div className="pointer-events-none absolute" style={dotsA} />
        <div className="pointer-events-none absolute" style={dotsB} />

        {/* brand */}
        <div className="relative flex items-center gap-[11px]">
          <div className="flex size-9 items-center justify-center rounded-[9px] border border-white/20 bg-white/[.12] text-[18px] font-bold">
            S
          </div>
          <div className="leading-[1.1]">
            <div className="text-[17px] font-bold -tracking-[.01em]">Skrezo</div>
            <div className="text-[10.5px] font-medium tracking-[.06em] text-white/55">ADMIN</div>
          </div>
        </div>

        {/* headline + footer */}
        <div className="relative">
          <h2 className="max-w-[360px] text-[25px] font-semibold leading-[1.25] -tracking-[.02em]">
            Центр управления
            <br />
            платформой Skrezo
          </h2>
          <p className="mt-3 max-w-[340px] text-[14px] leading-[1.5] text-white/60">
            Клиники, соло-врачи, тарифы и биллинг — в одном рабочем месте команды.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {SECTIONS.map((s) => (
              <span
                key={s}
                className="rounded-[20px] border border-white/15 px-[11px] py-1 text-[11.5px] text-white/60"
              >
                {s}
              </span>
            ))}
          </div>
          <div className="mt-7 flex items-center gap-2.5 font-mono text-[11.5px] text-white/40">
            <span>skrezo-admin · v1</span>
            <span className="size-[3px] rounded-full bg-white/30" />
            <span>© 2026</span>
          </div>
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-[372px]">
          <h1 className="text-[24px] font-bold -tracking-[.02em]">Вход в панель</h1>
          <p className="mt-2 text-[14px] leading-[1.45] text-muted-foreground">
            Телефон и пароль аккаунта команды Skrezo.
          </p>

          <form onSubmit={handleFormSubmit} className="mt-7 flex flex-col gap-4" noValidate>
            <Controller
              control={control}
              name="login"
              render={({field}) => (
                <div className="flex flex-col gap-[7px]">
                  <label className="text-[12.5px] font-semibold text-foreground/80">Телефон</label>
                  <div className="flex h-[46px] items-center gap-[9px] rounded-[10px] border border-input bg-card/60 px-[13px] transition focus-within:border-primary focus-within:bg-card focus-within:ring-[3px] focus-within:ring-primary/12">
                    <User
                      className="size-[17px] shrink-0 text-muted-foreground/70"
                      strokeWidth={1.8}
                    />
                    <span className="tnum shrink-0 text-[14.5px] font-medium text-foreground/80">
                      +996
                    </span>
                    <input
                      {...field}
                      value={formatKgPhone(field.value)}
                      onChange={handlePhoneChange(field.onChange)}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="700 000 000"
                      autoFocus
                      className="tnum h-full flex-1 border-none bg-transparent text-[14.5px] text-foreground outline-none placeholder:text-muted-foreground/70"
                    />
                  </div>
                </div>
              )}
            />

            <Controller
              control={control}
              name="password"
              render={({field}) => (
                <div className="flex flex-col gap-[7px]">
                  <label className="text-[12.5px] font-semibold text-foreground/80">Пароль</label>
                  <div className="flex h-[46px] items-center gap-[9px] rounded-[10px] border border-input bg-card/60 px-[13px] transition focus-within:border-primary focus-within:bg-card focus-within:ring-[3px] focus-within:ring-primary/12">
                    <Lock
                      className="size-[17px] shrink-0 text-muted-foreground/70"
                      strokeWidth={1.8}
                    />
                    <input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••••"
                      className="h-full flex-1 border-none bg-transparent text-[14.5px] tracking-[.02em] text-foreground outline-none placeholder:text-muted-foreground/70"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="flex items-center justify-center p-1 text-muted-foreground/70 transition-colors hover:text-muted-foreground"
                      aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                      {showPassword ? (
                        <EyeOff className="size-[18px]" strokeWidth={1.8} />
                      ) : (
                        <Eye className="size-[18px]" strokeWidth={1.8} />
                      )}
                    </button>
                  </div>
                </div>
              )}
            />

            <button
              type="submit"
              disabled={!canSubmit || mutation.isPending}
              className="mt-1.5 flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-primary text-[15px] font-semibold text-primary-foreground shadow-[0_3px_8px_rgba(44,92,219,.28)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="size-[17px] animate-spin" />
                  Входим…
                </>
              ) : (
                <>
                  Войти
                  <ArrowRight className="size-[17px]" strokeWidth={2.1} />
                </>
              )}
            </button>

            {/* Ошибка — под кнопкой, чтобы её появление не сдвигало поля ввода. */}
            {errorMessage && (
              <div className="rounded-[10px] border border-destructive/25 bg-destructive/10 px-3 py-2 text-center text-[13px] text-destructive">
                {errorMessage}
              </div>
            )}
          </form>

          <div className="mt-[26px] flex flex-col gap-3 border-t border-border pt-5">
            <div className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
              <Lock className="size-[13px] text-muted-foreground/60" strokeWidth={1.9} />
              Внутренняя панель · только для команды Skrezo
            </div>
            <Link
              to="/forgot-password"
              className="w-fit border-b border-border pb-px text-[13px] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              Забыли пароль? Сбросить по коду из SMS
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
